import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useApiState } from 'hooks/useApiState';
import type { DiagnosisRespondentRefResponseType } from 'types/api/diagnosisRespondentRefResponseType';
import {
  PAGE_LIMIT,
  computeHasNext,
  getPageEndNum,
  getPageStartNum,
} from 'pages/ws/utils/respondentPagination';

export interface UseWsRespondentDiagnosisResult {
  processing: boolean;
  diagnosis: DiagnosisRespondentRefResponseType | null;
  pageNo: number;
  hasNext: boolean;
  pageStartNum: number;
  pageEndNum: number;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  onModalReportDownloadOpen: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export function useWsRespondentDiagnosis(
  diagnosisCd: string | undefined
): UseWsRespondentDiagnosisResult {
  const { postDiagnosisRespondentRef } = useApiState();
  const { showBoundary } = useErrorBoundary();

  const [processing, setProcessing] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [diagnosis, setDiagnosis] = useState<DiagnosisRespondentRefResponseType | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const search = async (no: number) => {
    setProcessing(true);

    const response = await postDiagnosisRespondentRef({
      diagnosisCd: diagnosisCd,
      limit: PAGE_LIMIT,
      offset: (no - 1) * PAGE_LIMIT,
    });

    setDiagnosis(response);
    setPageNo(no);
    setHasNext(computeHasNext(no, PAGE_LIMIT, response.respondentCount));

    setProcessing(false);
  };

  useEffect(
    () => {
      // 初回マウント時に1ページ目を取得する。
      // search は useCallback 化されていないため render ごとに参照が変わり、
      // 依存に含めると毎 render 発火する。初回マウントのみ実行する移行元の挙動を
      // 保持するため意図的に除外している。
      (async () => {
        try {
          await search(1);
        } catch (e: unknown) {
          showBoundary(e);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onPrev = () => {
    if (pageNo <= 1) {
      return;
    }
    void search(pageNo - 1);
  };

  const onNext = () => {
    if (!hasNext) {
      return;
    }
    void search(pageNo + 1);
  };

  const onModalReportDownloadOpen = () => {
    setModalOpen(true);
  };

  // 派生値
  const pageStartNum = getPageStartNum(pageNo, PAGE_LIMIT);
  const pageEndNum = diagnosis
    ? getPageEndNum(pageNo, PAGE_LIMIT, hasNext, diagnosis.respondentCount)
    : 0;

  return {
    processing,
    diagnosis,
    pageNo,
    hasNext,
    pageStartNum,
    pageEndNum,
    modalOpen,
    setModalOpen,
    onModalReportDownloadOpen,
    onPrev,
    onNext,
  };
}
